import { useState, useCallback, useMemo, useEffect } from 'react'
// @mui
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Alert from '@mui/material/Alert'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Autocomplete from '@mui/material/Autocomplete'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Accordion from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails from '@mui/material/AccordionDetails'
import Badge from '@mui/material/Badge'
import Avatar from '@mui/material/Avatar'
import CircularProgress from '@mui/material/CircularProgress'
import Pagination from '@mui/material/Pagination'
import TablePagination from '@mui/material/TablePagination'

// hooks
import { useSettingsContext } from 'src/components/settings'
import { useAuthContext } from 'src/auth/hooks/use-auth-context'
import {
  usePendingValidation,
  useValidateTicket,
  useFiltersData,
  type TicketOrder,
  type TicketProduct,
} from 'src/service/ticket-validation.service'
import { useBoolean } from 'src/hooks/use-boolean'

// components
import Iconify from 'src/components/iconify'
import Scrollbar from 'src/components/scrollbar'
import { useSnackbar } from 'src/components/snackbar'
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs'
import { LoadingButton } from '@mui/lab'

// utils
import { fDate, fDateTime } from 'src/utils/format-time'
import { fCurrency } from 'src/utils/format-number'

// ----------------------------------------------------------------------

export default function TicketValidationView() {
  const settings = useSettingsContext()
  const { user } = useAuthContext()
  const { enqueueSnackbar } = useSnackbar()

  // Filter states
  const [filters, setFilters] = useState({
    event: null as any,
    product: null as any,
    status: 'all',
    search: '',
  })

  const [qrCodeInput, setQrCodeInput] = useState('')
  const [manualValidation, setManualValidation] = useState({
    orderNumber: '',
    arrayNumber: '',
  })

  // Track which tickets are being validated for UI feedback
  const [validatingTickets, setValidatingTickets] = useState<Set<string>>(new Set())

  const validateDialog = useBoolean()
  const validateMutation = useValidateTicket()

  // Fetch filters data
  const { data: filtersData, isLoading: isLoadingFilters } = useFiltersData()

  // Pagination and filters
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 10,
    sort: { createdAt: 'desc' as const },
  })

  // Applied filters for the query
  const appliedFilters = useMemo(
    () => ({
      event: filters.event?.value,
      product: filters.product?.value,
      status: filters.status !== 'all' ? filters.status : undefined,
      search: filters.search,
    }),
    [filters],
  )

  const {
    data: pendingData,
    isLoading,
    isFetching,
    refetch,
  } = usePendingValidation(pagination, appliedFilters)

  const handleValidateTicket = useCallback(async () => {
    try {
      if (qrCodeInput.trim()) {
        // Add to validating set
        setValidatingTickets((prev) => new Set(prev).add(qrCodeInput.trim()))

        // Validate using QR code hash
        await validateMutation.mutateAsync({
          hash: qrCodeInput.trim(),
        })
      } else if (manualValidation.orderNumber && manualValidation.arrayNumber) {
        // Create validation key for manual validation
        const validationKey = `${manualValidation.orderNumber}-${manualValidation.arrayNumber}`
        setValidatingTickets((prev) => new Set(prev).add(validationKey))

        // Validate using order number + array number
        await validateMutation.mutateAsync({
          orderNumber: manualValidation.orderNumber,
          arrayNumber: manualValidation.arrayNumber,
        })
      } else {
        enqueueSnackbar('Preencha o código QR ou os dados manuais', {
          variant: 'warning',
        })
        return
      }

      // Force refetch of pending validation data to get updated state
      refetch()

      // Reset form and close dialog
      setQrCodeInput('')
      setManualValidation({ orderNumber: '', arrayNumber: '' })
      validateDialog.onFalse()
    } catch (error) {
      // Error is handled by the mutation
    } finally {
      // Remove from validating set after completion/error
      if (qrCodeInput.trim()) {
        setValidatingTickets((prev) => {
          const newSet = new Set(prev)
          newSet.delete(qrCodeInput.trim())
          return newSet
        })
      } else if (manualValidation.orderNumber && manualValidation.arrayNumber) {
        const validationKey = `${manualValidation.orderNumber}-${manualValidation.arrayNumber}`
        setValidatingTickets((prev) => {
          const newSet = new Set(prev)
          newSet.delete(validationKey)
          return newSet
        })
      }
    }
  }, [qrCodeInput, manualValidation, validateMutation, validateDialog, enqueueSnackbar, refetch])

  const handleQuickValidate = useCallback(
    async (qrCodeHash: string) => {
      try {
        // Add to validating set for UI feedback
        setValidatingTickets((prev) => new Set(prev).add(qrCodeHash))

        await validateMutation.mutateAsync({
          hash: qrCodeHash,
        })

        // Force refetch of pending validation data to get updated state
        refetch()

        // Success feedback is handled by the mutation hook
      } catch (error) {
        // Error feedback is handled by the mutation hook
        console.error('Erro na validação:', error)
      } finally {
        // Remove from validating set
        setValidatingTickets((prev) => {
          const newSet = new Set(prev)
          newSet.delete(qrCodeHash)
          return newSet
        })
      }
    },
    [validateMutation, refetch],
  )

  const orders = pendingData?.data || []
  const stats = pendingData?.extraData

  // Pagination handlers
  const handlePageChange = useCallback((event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }))
  }, [])

  const handleRowsPerPageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPagination((prev) => ({
      ...prev,
      perPage: parseInt(event.target.value, 10),
      page: 1,
    }))
  }, [])

  // Reset pagination when filters change
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }))
  }, [appliedFilters])

  // Function to render orders content without nested ternary
  const renderOrdersContent = () => {
    if (isLoading || isFetching) {
      return (
        <Box sx={{ p: 3 }}>
          <LinearProgress />
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            Carregando tickets...
          </Typography>
        </Box>
      )
    }

    if (orders.length === 0) {
      const hasFilters =
        filters.event || filters.product || filters.status !== 'all' || filters.search

      return (
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <Iconify
            icon={hasFilters ? 'mdi:filter-outline' : 'mdi:ticket-confirmation-outline'}
            sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }}
          />
          <Typography variant='h6' color='text.secondary' gutterBottom>
            {hasFilters ? 'Nenhum resultado encontrado' : 'Nenhum ticket pendente de validação'}
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {hasFilters
              ? 'Tente ajustar os filtros para encontrar os tickets que procura.'
              : 'Todos os tickets que necessitam validação já foram processados.'}
          </Typography>
          {hasFilters && (
            <Button
              variant='outlined'
              startIcon={<Iconify icon='mdi:filter-remove' />}
              onClick={() => {
                setFilters({
                  event: null,
                  product: null,
                  status: 'all',
                  search: '',
                })
                setPagination((prev) => ({ ...prev, page: 1 }))
              }}
              sx={{ mt: 2 }}
            >
              Limpar Filtros
            </Button>
          )}
        </Box>
      )
    }

    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        {orders.map((order) => (
          <OrderCard
            key={order._id}
            order={order}
            onQuickValidate={handleQuickValidate}
            isValidating={validateMutation.isLoading}
            validatingTickets={validatingTickets}
          />
        ))}
      </Stack>
    )
  }

  return (
    <Container maxWidth={settings.themeStretch ? false : 'xl'}>
      <CustomBreadcrumbs
        heading='Validação de Ingressos'
        links={[
          { name: 'Dashboard', href: '/dashboard' },
          { name: 'Eventos', href: '/dashboard/events' },
          { name: 'Validação de Ingressos' },
        ]}
        action={
          <Stack direction='row' spacing={2}>
            <Tooltip title='Atualizar dados'>
              <IconButton
                onClick={() => refetch()}
                disabled={isFetching}
                color='primary'
                sx={{
                  '&:disabled': {
                    opacity: 0.7,
                  },
                }}
              >
                {isFetching ? <CircularProgress size={20} /> : <Iconify icon='mdi:refresh' />}
              </IconButton>
            </Tooltip>
            <Button
              variant='contained'
              startIcon={<Iconify icon='mdi:qrcode-scan' />}
              onClick={validateDialog.onTrue}
              color='primary'
            >
              Validar Ticket
            </Button>
          </Stack>
        }
      />

      {/* Advanced Filters */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Stack spacing={3}>
          <Typography variant='h6' sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Iconify icon='mdi:filter' />
            Filtros
          </Typography>

          <Grid container spacing={3}>
            {/* Event Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                value={filters.event}
                onChange={(_, newValue) => setFilters((prev) => ({ ...prev, event: newValue }))}
                options={filtersData?.events || []}
                getOptionLabel={(option) => option.label || ''}
                loading={isLoadingFilters}
                renderInput={(params) => <TextField {...params} label='Evento' fullWidth />}
              />
            </Grid>

            {/* Product Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                value={filters.product}
                onChange={(_, newValue) => setFilters((prev) => ({ ...prev, product: newValue }))}
                options={filtersData?.products || []}
                getOptionLabel={(option) => option.label || ''}
                loading={isLoadingFilters}
                renderInput={(params) => <TextField {...params} label='Produto' fullWidth />}
              />
            </Grid>

            {/* Status Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status de Validação</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
                  label='Status de Validação'
                >
                  <MenuItem value='all'>Todos</MenuItem>
                  <MenuItem value='pending'>Pendentes</MenuItem>
                  <MenuItem value='validated'>Validados</MenuItem>
                  <MenuItem value='partial'>Parcialmente Validados</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Search Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label='Buscar'
                placeholder='Número do pedido, produto...'
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                InputProps={{
                  startAdornment: (
                    <Iconify icon='mdi:magnify' sx={{ mr: 1, color: 'text.disabled' }} />
                  ),
                }}
              />
            </Grid>

            {/* Clear Filters */}
            <Grid item xs={12}>
              <Stack direction='row' spacing={2} justifyContent='space-between' alignItems='center'>
                <Button
                  variant='outlined'
                  color='inherit'
                  startIcon={<Iconify icon='mdi:filter-remove' />}
                  onClick={() => {
                    setFilters({
                      event: null,
                      product: null,
                      status: 'all',
                      search: '',
                    })
                    setPagination((prev) => ({ ...prev, page: 1 }))
                  }}
                >
                  Limpar Filtros
                </Button>

                {(filters.event ||
                  filters.product ||
                  filters.status !== 'all' ||
                  filters.search) && (
                  <Typography variant='body2' color='text.secondary'>
                    Filtros ativos:{' '}
                    {[
                      filters.event && 'Evento',
                      filters.product && 'Produto',
                      filters.status !== 'all' && 'Status',
                      filters.search && 'Busca',
                    ]
                      .filter(Boolean)
                      .join(', ')}
                  </Typography>
                )}
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Card>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant='h3' sx={{ color: 'error.main' }}>
                {stats.totalPendingTickets}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Tickets Pendentes
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant='h3' sx={{ color: 'success.main' }}>
                {stats.totalValidatedTickets}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Tickets Validados
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant='h3' sx={{ color: 'info.main' }}>
                {stats.totalTicketsRequiringValidation}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Total de Tickets
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant='h3' sx={{ color: 'warning.main' }}>
                {stats.validationProgress.toFixed(1)}%
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Progresso
              </Typography>
              <LinearProgress
                variant='determinate'
                value={stats.validationProgress}
                sx={{ mt: 1, height: 8, borderRadius: 4 }}
                color='warning'
              />
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Orders List */}
      <Card sx={{ position: 'relative' }}>
        {/* Loading overlay for refresh */}
        {isFetching && !isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1,
            }}
          >
            <Stack alignItems='center' spacing={1}>
              <CircularProgress size={40} />
              <Typography variant='body2' color='text.secondary'>
                Atualizando dados...
              </Typography>
            </Stack>
          </Box>
        )}

        {renderOrdersContent()}

        {/* Pagination */}
        {!isLoading && !isFetching && orders.length > 0 && (
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}
          >
            <Typography variant='body2' color='text.secondary'>
              Mostrando {orders.length} de {pendingData?.total || 0} pedidos
            </Typography>

            <Stack direction='row' alignItems='center' spacing={2}>
              <TablePagination
                component='div'
                count={pendingData?.total || 0}
                page={pagination.page - 1} // MUI usa 0-based
                onPageChange={(_, page) => handlePageChange(_, page + 1)} // Converter para 1-based
                rowsPerPage={pagination.perPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage='Itens por página:'
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                }
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    paddingLeft: 0,
                    paddingRight: 0,
                  },
                  '& .MuiTablePagination-selectLabel': {
                    marginBottom: 0,
                  },
                  '& .MuiTablePagination-displayedRows': {
                    marginBottom: 0,
                  },
                }}
              />
            </Stack>
          </Stack>
        )}
      </Card>

      {/* Validation Dialog */}
      <Dialog open={validateDialog.value} onClose={validateDialog.onFalse} maxWidth='sm' fullWidth>
        <DialogTitle>
          <Stack direction='row' alignItems='center' gap={1}>
            <Iconify icon='mdi:qrcode-scan' />
            Validar Ticket
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <Alert severity='info'>
              Escaneie o QR code do ticket ou insira os dados manualmente
            </Alert>

            <TextField
              fullWidth
              label='Código QR do Ticket'
              value={qrCodeInput}
              onChange={(e) => setQrCodeInput(e.target.value)}
              placeholder='Ex: 64a8f9b2c3d4e5f6-64a8f9b2c3d4e5f7'
              InputProps={{
                startAdornment: (
                  <Iconify icon='mdi:qrcode' sx={{ mr: 1, color: 'text.secondary' }} />
                ),
              }}
            />

            <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 1 }}>
              <Typography variant='body2'>- OU -</Typography>
            </Box>

            <Stack direction='row' spacing={2}>
              <TextField
                fullWidth
                label='Número do Pedido'
                value={manualValidation.orderNumber}
                onChange={(e) =>
                  setManualValidation((prev) => ({ ...prev, orderNumber: e.target.value }))
                }
                placeholder='12345678'
              />
              <TextField
                fullWidth
                label='Número do Produto'
                value={manualValidation.arrayNumber}
                onChange={(e) =>
                  setManualValidation((prev) => ({ ...prev, arrayNumber: e.target.value }))
                }
                placeholder='123456'
              />
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={validateDialog.onFalse}>Cancelar</Button>
          <LoadingButton
            variant='contained'
            onClick={handleValidateTicket}
            loading={validateMutation.isLoading}
            startIcon={!validateMutation.isLoading && <Iconify icon='mdi:check' />}
            disabled={
              !qrCodeInput.trim() &&
              (!manualValidation.orderNumber || !manualValidation.arrayNumber)
            }
          >
            {validateMutation.isLoading ? 'Validando...' : 'Validar Ticket'}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

// ----------------------------------------------------------------------

interface OrderCardProps {
  order: TicketOrder
  onQuickValidate: (qrCodeHash: string) => void
  isValidating: boolean
  validatingTickets: Set<string>
}

interface ProductValidationCardProps {
  product: TicketProduct
  onQuickValidate: (qrCodeHash: string) => void
  isValidating: boolean
  validatingTickets: Set<string>
}

function OrderCard({ order, onQuickValidate, isValidating, validatingTickets }: OrderCardProps) {
  const [expanded, setExpanded] = useState(false)

  // Safety check for order data
  if (!order || !order.orderValidationStatus) {
    return (
      <Paper variant='outlined' sx={{ p: 2 }}>
        <Alert severity='warning'>Dados do pedido incompletos ou corrompidos</Alert>
      </Paper>
    )
  }

  return (
    <Paper
      variant='outlined'
      sx={{
        p: 3,
        borderRadius: 2,
        '&:hover': {
          boxShadow: 2,
          borderColor: 'primary.main',
        },
        transition: 'all 0.2s ease-in-out',
      }}
    >
      {/* Order Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent='space-between'
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Stack spacing={1}>
          <Stack direction='row' alignItems='center' spacing={1}>
            <Typography variant='h6'>Pedido #{order.number}</Typography>
            <Chip
              label={order.orderValidationStatus?.isFullyValidated ? 'Validado' : 'Pendente'}
              color={order.orderValidationStatus?.isFullyValidated ? 'success' : 'warning'}
              size='small'
            />
          </Stack>

          <Stack direction='row' alignItems='center' spacing={2} flexWrap='wrap'>
            <Typography variant='body2' color='text.secondary'>
              <Iconify icon='mdi:calendar' sx={{ mr: 0.5 }} />
              {fDateTime(order.createdAt)}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              <Iconify icon='mdi:account' sx={{ mr: 0.5 }} />
              {order.saler?.name || 'Vendedor não informado'}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              <Iconify icon='mdi:map-marker' sx={{ mr: 0.5 }} />
              {order.event?.name || 'Evento não informado'}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction='row' alignItems='center' spacing={1}>
          <Button
            size='small'
            onClick={() => setExpanded(!expanded)}
            endIcon={<Iconify icon={expanded ? 'mdi:chevron-up' : 'mdi:chevron-down'} />}
          >
            {expanded ? 'Ocultar' : 'Ver'} Produtos
          </Button>
        </Stack>
      </Stack>

      {/* Progress Bar */}
      <Box sx={{ mb: 2 }}>
        <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1 }}>
          <Typography variant='body2' color='text.secondary'>
            Progresso da Validação
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            {order.orderValidationStatus?.totalValidated || 0}/
            {order.orderValidationStatus?.totalTickets || 0} tickets
          </Typography>
        </Stack>
        <LinearProgress
          variant='determinate'
          value={order.orderValidationStatus?.validationProgress || 0}
          sx={{ height: 8, borderRadius: 4 }}
          color={order.orderValidationStatus?.isFullyValidated ? 'success' : 'warning'}
        />
      </Box>

      {/* Products List */}
      {expanded && (
        <Stack spacing={2}>
          {order.products && order.products.length > 0 ? (
            order.products.map((product) => (
              <ProductValidationCard
                key={product._id}
                product={product}
                onQuickValidate={onQuickValidate}
                isValidating={isValidating}
                validatingTickets={validatingTickets}
              />
            ))
          ) : (
            <Alert severity='info'>Nenhum produto encontrado para este pedido</Alert>
          )}
        </Stack>
      )}
    </Paper>
  )
}

// ----------------------------------------------------------------------

function ProductValidationCard({
  product,
  onQuickValidate,
  isValidating,
  validatingTickets,
}: ProductValidationCardProps) {
  const [expandedTickets, setExpandedTickets] = useState(false)

  // Combine individual tickets and legacy scans for display
  const allValidations = useMemo(() => {
    // Safety check for product data inside useMemo
    if (!product) {
      return []
    }
    const validations: Array<{
      id: string
      type: 'individual' | 'legacy'
      ticketNumber?: number
      validatedBy: { _id: string; name: string }
      validatedAt: string
      isValidated: boolean
    }> = []

    // Add individual tickets (new system)
    if (product.tickets) {
      product.tickets.forEach((ticket, index) => {
        if (ticket.isValidated && ticket.validatedBy && ticket.validatedAt) {
          validations.push({
            id: `ticket-${index}`,
            type: 'individual',
            ticketNumber: ticket.ticketNumber,
            validatedBy: ticket.validatedBy,
            validatedAt: ticket.validatedAt,
            isValidated: true,
          })
        }
      })
    }

    // Add legacy scans (old system) for tickets not covered by individual system
    if (product.scans && product.scans.length > 0) {
      const individualValidatedCount = validations.length
      const legacyValidationsNeeded = product.scans.length - individualValidatedCount

      if (legacyValidationsNeeded > 0) {
        product.scans.slice(-legacyValidationsNeeded).forEach((scan, index) => {
          if (scan.scanedBy) {
            validations.push({
              id: `scan-${index}`,
              type: 'legacy',
              validatedBy: scan.scanedBy,
              validatedAt: scan.date,
              isValidated: true,
            })
          }
        })
      }
    }

    return validations.sort(
      (a, b) => new Date(b.validatedAt).getTime() - new Date(a.validatedAt).getTime(),
    )
  }, [product])

  // Safety check after hooks - create default values if missing
  if (!product) {
    return (
      <Paper variant='outlined' sx={{ p: 2 }}>
        <Alert severity='warning'>Produto não encontrado</Alert>
      </Paper>
    )
  }

  // Create default validationStatus if missing
  const safeProduct = {
    ...product,
    validationStatus: product.validationStatus || {
      totalTickets: product.quantity || 0,
      validatedTickets: 0,
      pendingTickets: product.quantity || 0,
      isFullyValidated: false,
      validationProgress: 0,
      qrCodeHash: `${product._id || 'unknown'}-fallback`,
    },
    tickets: product.tickets || [],
    scans: product.scans || [],
  }

  return (
    <Paper variant='outlined' sx={{ bgcolor: 'grey.50' }}>
      <Accordion
        expanded={expandedTickets}
        onChange={(_, isExpanded) => setExpandedTickets(isExpanded)}
      >
        <AccordionSummary
          expandIcon={<Iconify icon='eva:arrow-ios-downward-fill' />}
          sx={{ px: 2, py: 1.5 }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent='space-between'
            spacing={2}
            width='100%'
            pr={2}
          >
            <Stack spacing={1} flex={1}>
              <Stack direction='row' alignItems='center' spacing={2}>
                <Typography variant='subtitle2'>
                  {safeProduct.name || 'Produto sem nome'}
                </Typography>
                <Badge
                  badgeContent={allValidations.length}
                  color='primary'
                  max={99}
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }}
                >
                  <Iconify icon='mdi:ticket-confirmation' sx={{ color: 'primary.main' }} />
                </Badge>
              </Stack>

              <Stack direction='row' alignItems='center' spacing={2} flexWrap='wrap'>
                <Typography variant='body2' color='text.secondary'>
                  Quantidade: {safeProduct.quantity || 0}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Preço: {fCurrency((safeProduct.price || 0) / 100)}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Validados: {safeProduct.validationStatus.validatedTickets}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Pendentes: {safeProduct.validationStatus.pendingTickets}
                </Typography>
              </Stack>

              <LinearProgress
                variant='determinate'
                value={safeProduct.validationStatus.validationProgress}
                sx={{ height: 6, borderRadius: 3, maxWidth: 200 }}
                color={safeProduct.validationStatus.isFullyValidated ? 'success' : 'info'}
              />
            </Stack>

            <Stack direction='row' alignItems='center' spacing={1}>
              <Chip
                label={safeProduct.validationStatus.isFullyValidated ? 'Completo' : 'Pendente'}
                color={safeProduct.validationStatus.isFullyValidated ? 'success' : 'warning'}
                size='small'
              />

              {!safeProduct.validationStatus.isFullyValidated && (
                <Tooltip title='Validar próximo ticket'>
                  <IconButton
                    size='small'
                    color='primary'
                    onClick={(e) => {
                      e.stopPropagation()
                      onQuickValidate(safeProduct.validationStatus.qrCodeHash)
                    }}
                    disabled={
                      isValidating || validatingTickets.has(safeProduct.validationStatus.qrCodeHash)
                    }
                    sx={{
                      position: 'relative',
                      '&:disabled': {
                        color: 'primary.main',
                        opacity: 0.7,
                      },
                    }}
                  >
                    {(() => {
                      if (validatingTickets.has(safeProduct.validationStatus.qrCodeHash)) {
                        return <CircularProgress size={20} color='primary' />
                      }

                      return <Iconify icon='mdi:qrcode-scan' />
                    })()}
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        </AccordionSummary>

        <AccordionDetails sx={{ px: 2, pb: 2 }}>
          <Stack spacing={3}>
            {/* Todos os Tickets */}
            {safeProduct.tickets && safeProduct.tickets.length > 0 && (
              <Box>
                <Typography variant='subtitle2' color='text.primary' sx={{ mb: 2 }}>
                  🎫 Todos os Tickets ({safeProduct.tickets.length})
                  <Chip
                    label={`${safeProduct.validationStatus.validatedTickets} validados`}
                    size='small'
                    color='success'
                    sx={{ ml: 1, fontSize: '0.7rem' }}
                  />
                  <Chip
                    label={`${safeProduct.validationStatus.pendingTickets} pendentes`}
                    size='small'
                    color='warning'
                    sx={{ ml: 0.5, fontSize: '0.7rem' }}
                  />
                </Typography>

                <Grid container spacing={1}>
                  {safeProduct.tickets
                    .sort((a, b) => (a.ticketNumber || 0) - (b.ticketNumber || 0)) // Ordenar por número
                    .map((ticket) => {
                      const { isValidated } = ticket
                      const isTicketValidating = validatingTickets.has(ticket.qrCodeHash || '')

                      return (
                        <Grid item xs={6} sm={4} md={3} key={ticket.ticketNumber || Math.random()}>
                          <Paper
                            variant='outlined'
                            sx={{
                              p: 1.5,
                              textAlign: 'center',
                              bgcolor: isValidated ? 'success.lighter' : 'warning.lighter',
                              border: '1px solid',
                              borderColor: isValidated ? 'success.main' : 'warning.main',
                              borderRadius: 2,
                              cursor: isValidated ? 'default' : 'pointer',
                              opacity: isTicketValidating ? 0.7 : 1,
                              ...(!isValidated && {
                                '&:hover': {
                                  bgcolor: 'warning.light',
                                  transform: 'scale(1.02)',
                                },
                              }),
                              transition: 'all 0.2s ease-in-out',
                            }}
                            onClick={
                              isValidated || !ticket.qrCodeHash
                                ? undefined
                                : () => onQuickValidate(ticket.qrCodeHash)
                            }
                          >
                            <Stack alignItems='center' spacing={0.5}>
                              {isTicketValidating ? (
                                <CircularProgress size={20} color='primary' />
                              ) : (
                                <Iconify
                                  icon={
                                    isValidated ? 'mdi:ticket-confirmation' : 'mdi:ticket-outline'
                                  }
                                  sx={{
                                    fontSize: 20,
                                    color: isValidated ? 'success.dark' : 'warning.dark',
                                  }}
                                />
                              )}
                              <Typography
                                variant='body2'
                                fontWeight={600}
                                color={isValidated ? 'success.dark' : 'warning.dark'}
                              >
                                #{ticket.ticketNumber || 'N/A'}
                              </Typography>
                              <Typography
                                variant='caption'
                                color='text.secondary'
                                sx={{ fontSize: '0.7rem' }}
                              >
                                {(() => {
                                  if (isTicketValidating) return 'Validando...'
                                  if (isValidated && ticket.validatedAt)
                                    return fDateTime(ticket.validatedAt)
                                  return 'Clique para validar'
                                })()}
                              </Typography>
                              {isValidated && ticket.validatedBy && (
                                <Typography
                                  variant='caption'
                                  color='success.dark'
                                  sx={{ fontSize: '0.65rem', fontWeight: 500 }}
                                >
                                  por {ticket.validatedBy?.name || 'Usuário não informado'}
                                </Typography>
                              )}
                            </Stack>
                          </Paper>
                        </Grid>
                      )
                    })}
                </Grid>
              </Box>
            )}

            {/* Histórico de Validações */}
            {allValidations.length > 0 ? (
              <Stack spacing={2}>
                <Typography variant='subtitle2' color='text.primary'>
                  ✅ Histórico de Validações ({allValidations.length})
                </Typography>

                <Stack spacing={1.5}>
                  {allValidations.map(
                    (validation: {
                      id: string
                      type: 'individual' | 'legacy'
                      ticketNumber?: number
                      validatedBy: { _id: string; name: string }
                      validatedAt: string
                      isValidated: boolean
                    }) => (
                      <Paper
                        key={validation.id}
                        variant='outlined'
                        sx={{
                          p: 2,
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'success.lighter',
                        }}
                      >
                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          alignItems={{ xs: 'flex-start', sm: 'center' }}
                          justifyContent='space-between'
                          spacing={1}
                        >
                          <Stack direction='row' alignItems='center' spacing={2}>
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                bgcolor: 'success.lighter',
                                color: 'success.dark',
                                fontSize: '0.875rem',
                              }}
                            >
                              {validation.validatedBy?.name?.charAt(0)?.toUpperCase() || '?'}
                            </Avatar>

                            <Stack spacing={0.5}>
                              <Stack direction='row' alignItems='center' spacing={1}>
                                <Typography variant='body2' fontWeight={600}>
                                  {validation.validatedBy?.name || 'Usuário não informado'}
                                </Typography>
                                {validation.type === 'individual' && validation.ticketNumber && (
                                  <Chip
                                    label={`Ticket #${validation.ticketNumber}`}
                                    size='small'
                                    color='primary'
                                    variant='outlined'
                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                  />
                                )}
                                {validation.type === 'legacy' && (
                                  <Chip
                                    label='Validação Legada'
                                    size='small'
                                    color='info'
                                    variant='outlined'
                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                  />
                                )}
                              </Stack>

                              <Typography variant='caption' color='text.secondary'>
                                <Iconify icon='mdi:clock-outline' sx={{ mr: 0.5, fontSize: 12 }} />
                                {fDateTime(validation.validatedAt)}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack direction='row' alignItems='center' spacing={1}>
                            <Chip
                              label='Validado'
                              size='small'
                              color='success'
                              icon={<Iconify icon='mdi:check-circle' />}
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </Stack>
                        </Stack>
                      </Paper>
                    ),
                  )}
                </Stack>
              </Stack>
            ) : (
              <Alert severity='warning' sx={{ bgcolor: 'warning.lighter' }}>
                <Typography variant='body2'>
                  Nenhuma validação encontrada para este produto. Todos os{' '}
                  <strong>{safeProduct.quantity || 0} ticket(s)</strong> ainda estão pendentes.
                </Typography>
              </Alert>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Paper>
  )
}
